export function extractRepeatDateFromEventId(eventId: string): string | null {
  if (!eventId.includes(':repeat:')) return null;
  const parts = eventId.split(':repeat:');
  const dateStr = parts[1] ?? '';
  return dateStr.length > 0 ? dateStr : null;
}

export function getNavigationDateFromEventId(
  eventId: string,
  itemScheduledAt: string | undefined
): Date | null {
  const repeatDateStr = extractRepeatDateFromEventId(eventId);
  if (repeatDateStr) {
    const parts = repeatDateStr.split('-').map(Number);
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    if (
      year !== undefined &&
      month !== undefined &&
      day !== undefined &&
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day)
    ) {
      return new Date(year, month - 1, day);
    }
  }
  if (itemScheduledAt) {
    const date = new Date(itemScheduledAt);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export interface EventTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

export function getEventStartParts(
  event: { date: string; startTime: string }
): EventTimeParts | null {
  const dateParts = event.date.split('-').map(Number);
  const timeParts = event.startTime.split(':').map(Number);
  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2];
  const hour = timeParts[0];
  const minute = timeParts[1];
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  return { year, month, day, hour, minute };
}

export function getEventDurationMinutes(
  event: { startTime: string; endTime: string }
): number | null {
  const startParts = event.startTime.split(':').map(Number);
  const endParts = event.endTime.split(':').map(Number);
  const startHour = startParts[0];
  const startMinute = startParts[1];
  const endHour = endParts[0];
  const endMinute = endParts[1];
  if (
    startHour === undefined ||
    startMinute === undefined ||
    endHour === undefined ||
    endMinute === undefined ||
    !Number.isFinite(startHour) ||
    !Number.isFinite(startMinute) ||
    !Number.isFinite(endHour) ||
    !Number.isFinite(endMinute)
  ) {
    return null;
  }
  const durationMinutes =
    endHour * 60 + endMinute - (startHour * 60 + startMinute);
  return Number.isFinite(durationMinutes) && durationMinutes > 0
    ? durationMinutes
    : null;
}

export function parseCalendarSlotId(
  slotId: string,
  prefix: string
): { dateStr: string; hour: number; minute: number } | null {
  if (!slotId.startsWith(prefix)) return null;
  const raw = slotId.slice(prefix.length);
  const parts = raw.split(':');
  const dateStr = parts[0];
  const hourStr = parts[1];
  const minuteStr = parts[2];
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (!dateStr || !Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  return { dateStr, hour, minute };
}

export function buildSlotDate(
  dateStr: string,
  hour: number,
  minute: number
): Date | null {
  const parts = dateStr.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return null;
  }
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  return isNaN(date.getTime()) ? null : date;
}

export function getCompletedDateKey(
  eventId: string,
  fallbackDate?: Date
): string | null {
  const repeatDateStr = extractRepeatDateFromEventId(eventId);
  if (repeatDateStr) {
    return repeatDateStr;
  }
  if (fallbackDate && !isNaN(fallbackDate.getTime())) {
    const year = fallbackDate.getFullYear();
    const month = String(fallbackDate.getMonth() + 1).padStart(2, '0');
    const day = String(fallbackDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
}

export function isRecurringEventCompleted(
  eventId: string,
  completedDates: Record<string, boolean> | undefined
): boolean {
  const dateKey = extractRepeatDateFromEventId(eventId);
  if (!dateKey) return false;
  if (!completedDates) return false;
  return completedDates[dateKey] === true;
}

export function isRecurringEventId(eventId: string | undefined): boolean {
  if (!eventId || eventId.length === 0) return false;
  return eventId.includes(':repeat:');
}

export function parseTimeToHoursMinutes(
  time: string
): { hour: number; minute: number } | null {
  if (!time || !time.includes(':')) return null;
  const parts = time.split(':');
  const hourStr = parts[0];
  const minuteStr = parts[1];
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour, minute };
}
